import { useEffect, useState } from "react";
import {
  listManifests,
  listProvenanceEvents,
  ManifestRow,
  ProvenanceRow,
} from "@/api/methodologyConsole";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { UNKNOWN_VALUE_LABEL } from "@/config/intelligenceFlags";
import {
  EmptyState,
  ErrorState,
  HashLabel,
  LoadingState,
} from "./primitives";

const EVENT_LABELS: Record<string, string> = {
  respondent_submission: "Respondent submission",
  reviewer_completion: "Reviewer completion",
  approval_decision: "Approval decision",
  cap_publication: "Corrective action plan published",
  report_generation: "Report generated",
  report_regeneration: "Report regenerated",
};

const unknown = (v: unknown) =>
  v === null || v === undefined || v === "" ? (
    <span className="italic text-muted-foreground">{UNKNOWN_VALUE_LABEL}</span>
  ) : (
    <>{String(v)}</>
  );

function ProvenanceTimeline({ assessmentId }: { assessmentId: string }) {
  const [rows, setRows] = useState<ProvenanceRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setRows(null);
    listProvenanceEvents(assessmentId)
      .then(setRows)
      .catch((e: Error) => setError(e.message));
  }, [assessmentId]);

  if (error) return <ErrorState message={error} />;
  if (!rows) return <LoadingState label="Loading provenance…" />;
  if (rows.length === 0)
    return (
      <EmptyState
        title="No structured provenance"
        description="Legacy assessment — structured provenance was not captured."
      />
    );

  return (
    <ol className="space-y-3 border-l pl-4">
      {rows.map((e) => (
        <li key={e.id} className="relative">
          <span className="absolute -left-[21px] top-1.5 h-2 w-2 rounded-full bg-primary" />
          <p className="text-sm font-medium">
            {EVENT_LABELS[e.event_kind] ?? e.event_kind.replace(/_/g, " ")}
          </p>
          <p className="text-xs text-muted-foreground">
            {new Date(e.occurred_at).toLocaleString("en-US")}
            {e.actor_role ? ` · ${e.actor_role}` : ""}
            {e.event_status ? ` · ${e.event_status}` : ""}
          </p>
        </li>
      ))}
    </ol>
  );
}

export default function ManifestsTab() {
  const [rows, setRows] = useState<ManifestRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<ManifestRow | null>(null);

  useEffect(() => {
    listManifests(100, 0)
      .then(setRows)
      .catch((e: Error) => setError(e.message));
  }, []);

  if (error) return <ErrorState message={error} />;
  if (!rows) return <LoadingState label="Loading assessment manifests…" />;
  if (rows.length === 0)
    return (
      <EmptyState
        title="No assessment manifests"
        description="No Assessment Manifest (internally: Assessment DNA) record exists. Manifests are generated by governed server procedures, never by this console."
      />
    );

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Assessment Manifests (internal architecture term: Assessment DNA). Missing historical
        values are shown as “{UNKNOWN_VALUE_LABEL}” and are never inferred or backfilled.
      </p>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Portfolio company</TableHead>
            <TableHead>Kind</TableHead>
            <TableHead>Version</TableHead>
            <TableHead>Questionnaire</TableHead>
            <TableHead>Questions</TableHead>
            <TableHead>Content hash</TableHead>
            <TableHead>Frozen</TableHead>
            <TableHead className="text-right">Detail</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.id}>
              <TableCell className="font-medium">
                {unknown(r.portfolio_name)}
              </TableCell>
              <TableCell>
                <Badge variant={r.record_kind === "native" ? "default" : "secondary"}>
                  {r.record_kind === "native"
                    ? "Native"
                    : r.record_kind === "reconstructed"
                      ? "Reconstructed"
                      : "Unknown"}
                </Badge>
              </TableCell>
              <TableCell>{r.dna_version}</TableCell>
              <TableCell className="text-sm">{unknown(r.questionnaire_id)}</TableCell>
              <TableCell>{unknown(r.question_count)}</TableCell>
              <TableCell>
                <HashLabel value={r.content_hash} />
              </TableCell>
              <TableCell className="text-sm">
                {r.frozen_at ? new Date(r.frozen_at).toLocaleDateString("en-US") : "No"}
              </TableCell>
              <TableCell className="text-right">
                <Button size="sm" variant="outline" onClick={() => setOpen(r)}>
                  View
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Sheet open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-2xl">
          <SheetHeader>
            <SheetTitle>Assessment Manifest</SheetTitle>
            <SheetDescription>
              Read-only record. Historical gaps are displayed, never reconstructed.
            </SheetDescription>
          </SheetHeader>
          {open && (
            <div className="mt-4 space-y-5">
              <div className="text-sm">
                {[
                  ["Record kind", open.record_kind],
                  ["Manifest status", open.status],
                  ["Manifest version", open.dna_version],
                  ["Current", open.is_current ? "Yes" : "No"],
                  ["Questionnaire", open.questionnaire_id],
                  ["Question count", open.question_count],
                  ["Portfolio company", open.portfolio_name],
                  ["Generated", open.generated_at],
                  ["Frozen", open.frozen_at],
                ].map(([label, value]) => (
                  <div key={String(label)} className="flex gap-3 border-b py-1.5 last:border-0">
                    <span className="w-44 shrink-0 text-muted-foreground">{label}</span>
                    <span className="min-w-0 break-words">{unknown(value)}</span>
                  </div>
                ))}
                <div className="flex gap-3 border-b py-1.5">
                  <span className="w-44 shrink-0 text-muted-foreground">Content hash</span>
                  <HashLabel value={open.content_hash} />
                </div>
              </div>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Provenance timeline</CardTitle>
                </CardHeader>
                <CardContent>
                  <ProvenanceTimeline assessmentId={open.assessment_id} />
                </CardContent>
              </Card>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
